import LensFilterReceipt, { type LensFiltered } from '~/pages/LeLens/LensFilterReceipt';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import LensChangeReceipt from '~/pages/LeLens/LensChangeReceipt';
import AnswerLensUndo from '~/pages/LeLens/AnswerLensUndo';
import LensConflictReceipt, { type LensConflict } from '~/pages/LeLens/LensConflictReceipt';
import LensPresentationProposal from '~/pages/LeLens/LensPresentationProposal';
import LensProposalPreview from '~/pages/LeLens/LensProposalPreview';
import { resolveMetricHeadline } from '~/pages/LeLens/cards/metricValue';
import { resolveChatResultForPin } from '~/pages/LeLens/chatResultForPin';
import { fieldFormat, formatLensValue, isDateLikeText, isMeasureColumn } from '~/pages/LeLens/lensChart';
import { parseLensPresentationEdits } from '~/pages/LeLens/presentationEdit';

import type { LensAppliedChange } from '~/hooks/apis/reporting/useLensChange';
import { useReportingLeDataExport } from '~/hooks/apis/reporting/useReportingLeDataExport';
import useCanAccessLeLens from '~/hooks/useCanAccessLeLens';

import type {
  LeDataContentBlock,
  LeDataLensCardSuggestion,
  LensIntent,
  LensScopeBlockPayload,
} from '~/types/leData';
import { type Lens, type LensCard, type LensCardType } from '~/types/leLens';

import { downloadCsvFile } from '~/utils/csvDownload';
import { isThoughtProcessBlock } from '~/utils/leData/blockGrouping';
import { normalizeTableBlock } from '~/utils/leData/tableBlock';

import ContentBlockView from './ContentBlockView';
import LeDataMarkdownPreview from './LeDataMarkdownPreview';
import LensCreatedReceipt from './LensCreatedReceipt';
import LensScopeBlock from './LensScopeBlock';
import { SaveSqlToLensButton, SaveSqlToLensDialog, inferLensCardFromSql } from './SqlCodeMirrorBlock';
import { createdReceiptLensId, latestCreatedReceiptId } from './createdLensReceipt';
import { dashboardBriefFromAnswer } from './dashboardBrief';
import { type DashboardDestinations, matchDashboardForSuggestion } from './dashboardDestinations';
import { extractFencedCodeSegments } from './extractFencedCodeSegments';
import { shownLensDraftBlockIds } from './globalLensDraft';
import { isExecutableLensSql } from './lensSql';

const BLOCK_ID_FILENAME_LENGTH = 8;
const TAB_FONT_SIZE_PX = 13;
const MAX_DASHBOARD_REVIEW_CONTEXT_LENGTH = 64000;
/** Visible turn text for the "Build Lens from this answer" button (chat/LeDataMessageBlocks.tsx's
 *  own precedent for a product control's fixed request text: see SAVE_ONE_CARD_REQUEST in
 *  LeDataChat.tsx). The brief itself, which can carry hidden planner instructions and evidence, is
 *  never shown as the reader's own words. */
const BUILD_LENS_FROM_ANSWER_QUESTION = 'Build a Lens from this answer.';

type Props = {
  /** Stamps `data-anchor-id` on the evidence card, so the streaming read-position anchor in
   *  LeDataChat can distinguish this message's card from another message's. */
  messageId: string;
  blocks: Array<LeDataContentBlock>;
  /** The user turn that produced this answer. Kept separate from answer evidence so the
   *  dashboard review can show the person's intent instead of planner instructions. */
  sourceQuestion?: string;
  onSectionToggle?: () => void;
  /**
   * While the answer is still streaming the `table` block can arrive before the `chart` block,
   * so `hasChart` is briefly false and the Data tab would auto-open then collapse once the chart
   * lands. Defer the auto-open decision until streaming settles so the final layout appears once.
   */
  isStreaming?: boolean;
  textStreaming?: boolean;
  /** Embedded-context only: forwarded to each SQL-tab block's "Keep this live" pin. Absent everywhere
   *  else, including /ask-richard, so the pin never renders there. */
  onKeepLiveSql?: (sqlText: string, blockId: string) => void;
  /** Lens-scoped chats use a labelled save action in each SQL toolbar. */
  prominentLensSave?: boolean;
  /** The open Lens and cards that a chat result may deliberately replace. */
  lensSaveTarget?: { lensId: string; cards: ReadonlyArray<{ id: string; title: string }> };
  /** Global conversations may review only server-backed candidates with their own Lens target. */
  allowGlobalLensDrafts?: boolean;
  /**
   * A card explicitly selected in the Lens before the question was asked. This
   * is intent, not a model guess: keep the proposed save pointed at it even if
   * the answer prose describes the result as a new KPI.
   */
  forcedReplacementCard?: { id: string; title: string };
  /**
   * The card-scoped Ask pane's own apply action: present only when this answer's SQL is being
   * offered to the one card the pane is open for. Replaces the destination dialog's
   * "Replace <card>" with a direct, no-dialog apply into the pane's own draft; supersedes
   * `forcedReplacementCard` when both are given.
   */
  cardApply?: {
    card: LensCard;
    onApplyProposal: (card: LensCard) => void;
    onSaveDraft: () => Promise<void>;
    dirty: boolean;
    isSaving: boolean;
  };
  /** A current dashboard or carefully matched certified dashboard that can be opened from this answer. */
  dashboardDestinations?: DashboardDestinations;
  /** Called after the Lens API confirms that this answer is now durable. */
  onSavedLens?: (lens: Lens) => void;
  /** A safe recovery action for Lens answers that contain a result but no executable query. */
  onCreateLensCard?: () => void;
  /** Starts the reviewed dashboard journey, retaining whether the answer already has executable results. */
  onBuildDashboard?: (
    brief: string,
    context?: { hasExecutedResults: boolean; newLens?: boolean; sourceQuestion?: string },
  ) => void;
  latestLensScopeBlockId?: string | null;
  /** Only the latest saved creation receipt in the thread can report unfinished cards. */
  latestLensCreatedBlockId?: string | null;
  lensTurnSending?: boolean;
  /** The reader's visible answer to this turn's scope question: in flight, or the reply that followed it. */
  lensTurnAnswerText?: string;
  lensTurnQuestionAnswer?: Extract<LensIntent, { kind: 'answer' }>['questionAnswer'];
  /** Sends one turn for a lens_scope chip, a geography value, or the "Build Lens from this answer"
   *  button: `question` is the reader-visible turn text, `intent` carries the LensIntent. Falls
   *  back to the older `onBuildDashboard` modal path when absent. */
  onSendLensTurn?: (question: string, intent: LensIntent) => void;
  onLensCreated?: (lensId: string) => void;
  /** A compact host surface can suppress the in-answer working shelf. */
  hideEvidence?: boolean;
  /** The parent answer card owns the only outer edge, so inline blocks stay unframed. */
  frameless?: boolean;
  /** Render just one evidence section in a host-owned working surface. */
  evidenceOnly?: boolean;
  evidenceTab?: EvidenceTabId;
  actionRow?: (doors: AnswerDoors) => React.ReactNode;
};

export interface AnswerDoors {
  buildLens?: () => void;
  addToLens?: Omit<React.ComponentProps<typeof SaveSqlToLensDialog>, 'open' | 'onClose'>;
  downloadTable?: () => void;
}

function answerTitleOf(blocks: Array<LeDataContentBlock>): string | undefined {
  return blocks
    .map((block) => block.payload?.title)
    .reverse()
    .find((title): title is string => typeof title === 'string' && Boolean(title.trim()));
}

/** The three evidence disclosures live under one card as text tabs, in this fixed order. */
export type EvidenceTabId = 'sql' | 'data' | 'reasoning';

export type EvidenceTab = {
  id: EvidenceTabId;
  label: string;
  shortLabel: string;
};

type PartitionedBlocks = {
  /** Prose, charts, KPIs, files, errors: the answer itself, rendered on the canvas. */
  inline: Array<LeDataContentBlock>;
  sql: Array<LeDataContentBlock>;
  table: Array<LeDataContentBlock>;
  thought: Array<LeDataContentBlock>;
};

const RECEIPT_BLOCK_TYPES = new Set(['lens_draft', 'lens_applied', 'lens_conflict']);

/**
 * Splits a message's blocks into the answer prose (inline) and the three evidence groups
 * (SQL / data tables / reasoning) that fold into the single artifact card. Thought-process
 * blocks are `text` blocks with a section marker, so they must be checked before the plain
 * inline fallback.
 */
function partitionBlocks(blocks: Array<LeDataContentBlock>): PartitionedBlocks {
  const partitioned: PartitionedBlocks = { inline: [], sql: [], table: [], thought: [] };

  for (const block of blocks) {
    if (isThoughtProcessBlock(block)) {
      partitioned.thought.push(block);
    } else if (block.type === 'sql') {
      partitioned.sql.push(block);
    } else if (block.type === 'table') {
      partitioned.table.push(block);
    } else {
      partitioned.inline.push(block);
    }
  }

  // Streamed answers may put their SQL fence in prose before a dedicated SQL
  // block arrives. Promote that fence into the working shelf so implementation
  // details never need to appear in the answer itself.
  if (partitioned.sql.length === 0) {
    for (const block of partitioned.inline) {
      if (block.type !== 'text') {
        continue;
      }
      extractFencedCodeSegments(pickText(block.payload)).forEach((segment, index) => {
        if (segment.kind !== 'code' || !/^(sql|bigquery|bq)$/i.test(segment.language) || !segment.content.trim()) {
          return;
        }
        partitioned.sql.push({
          id: `${block.id}-streamed-sql-${index}`,
          type: 'sql',
          payload: { sql: segment.content.trim() },
        });
      });
    }
  }

  return partitioned;
}

/** Available evidence sections for a settled answer, shared by the inline and side-pane views. */
export function getEvidenceTabs(blocks: Array<LeDataContentBlock>): Array<EvidenceTab> {
  return buildEvidenceTabs(partitionBlocks(blocks)).map((tab) => ({
    ...tab,
    shortLabel: tab.id === 'reasoning' ? 'Thought process' : tab.id === 'data' ? 'Data' : 'SQL',
  }));
}

function pickText(payload: Record<string, unknown>): string {
  for (const key of ['markdown', 'text', 'content', 'message', 'body']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
}

/**
 * A chart is only meaningful when readers can compare at least two labelled
 * values. A one-row aggregate belongs in a metric or table, not an empty axis
 * frame pretending to be a trend. Keep this shape check beside the message
 * policy so the answer and its Data disclosure make the same decision.
 */
function isRenderableChartPayload(payload: Record<string, unknown>): boolean {
  const data = payload.data;
  if (
    !Array.isArray(data) ||
    data.length < 2 ||
    !data.every((row) => row && typeof row === 'object' && !Array.isArray(row))
  ) {
    return false;
  }

  const rows = data as Array<Record<string, unknown>>;
  const xKey =
    (typeof payload.xKey === 'string' && payload.xKey) ||
    (typeof payload.labelKey === 'string' && payload.labelKey) ||
    'name';
  const requestedSeries = ['seriesKeys', 'keys', 'yKeys']
    .flatMap((key) => (Array.isArray(payload[key]) ? payload[key] : []))
    .filter((key): key is string => typeof key === 'string' && key.trim().length > 0);
  const requestedY =
    (typeof payload.yKey === 'string' && payload.yKey) ||
    (typeof payload.valueKey === 'string' && payload.valueKey) ||
    undefined;
  const candidateSeries = requestedSeries.length
    ? requestedSeries
    : requestedY
      ? [requestedY]
      : [...new Set(rows.flatMap((row) => Object.keys(row)))].filter((key) => key !== xKey && key !== 'id');
  const hasLabels =
    new Set(
      rows
        .map((row) => row[xKey])
        .filter(
          (value): value is string | number | boolean =>
            (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') &&
            String(value).trim() !== '',
        )
        .map((value) => String(value)),
    ).size >= 2;
  const hasNumericSeries = candidateSeries.some((key) =>
    rows.some(
      (row) => row[key] !== '' && row[key] !== null && row[key] !== undefined && Number.isFinite(Number(row[key])),
    ),
  );

  return hasLabels && hasNumericSeries;
}

function hasRenderableChartFence(raw: string): boolean {
  return extractFencedCodeSegments(raw).some((segment) => {
    if (segment.kind !== 'code' || segment.language !== 'chart') {
      return false;
    }
    try {
      const parsed = JSON.parse(segment.content.trim());
      return (
        !!parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        isRenderableChartPayload(parsed as Record<string, unknown>)
      );
    } catch {
      return false;
    }
  });
}

/** Preserve the answer's business context while making the planner, rather than raw SQL order,
 * choose the final user-facing results for review. */
/**
 * Persist the SQL that produced the answer, not the first exploratory query
 * the agent happened to run. A server-provided Lens suggestion is the
 * strongest association; otherwise the final executed statement is the one
 * whose result the answer is presenting.
 */
export function getPrimarySql(
  partitioned: PartitionedBlocks,
): { sql: string; lensCardSuggestion?: LeDataLensCardSuggestion } | null {
  const candidates: Array<{ sql: string; lensCardSuggestion?: LeDataLensCardSuggestion }> = [];
  for (const block of partitioned.sql) {
    for (const key of ['sql', 'content', 'query', 'statement', 'body']) {
      const value = block.payload[key];
      if (typeof value === 'string' && isExecutableLensSql(value)) {
        const lensCard = block.payload.lensCard;
        candidates.push({
          sql: value,
          ...(isLensCardSuggestion(lensCard) ? { lensCardSuggestion: lensCard } : {}),
        });
        break;
      }
    }
  }
  if (candidates.length) {
    const explicitlySaveable = [...candidates].reverse().find((candidate) => candidate.lensCardSuggestion);
    return explicitlySaveable ?? candidates[candidates.length - 1];
  }

  // Some model responses put the executable query directly in prose instead
  // of returning a dedicated SQL evidence block. It is still a legitimate
  // answer asset, so surface the same clear dashboard decision rather than
  // leaving people to discover a toolbar icon inside the message.
  for (const block of partitioned.inline) {
    if (block.type !== 'text') {
      continue;
    }
    const segment = [...extractFencedCodeSegments(pickText(block.payload))]
      .reverse()
      .find((candidate) => candidate.kind === 'code' && /^(sql|bigquery|bq)$/i.test(candidate.language));
    if (segment?.content && isExecutableLensSql(segment.content)) {
      return { sql: segment.content.trim() };
    }
  }
  return null;
}

function isLensCardSuggestion(value: unknown): value is LeDataLensCardSuggestion {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.title === 'string' && typeof candidate.visualType === 'string';
}

type LensProposalPreview = {
  table: LeDataContentBlock;
  chart: LeDataContentBlock;
  metric: { title: string; value: string; detail?: string } | null;
};

function AddCardIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24">
      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2.6" />
    </SvgIcon>
  );
}

/**
 * A saved-card proposal should be judged from the result, not from its implementation.
 * Reuse the returned table rows to draw the candidate immediately; the SQL remains in the
 * evidence drawer for the rare moment it is useful. This deliberately makes no request of
 * its own, so the preview is always the exact rows the assistant just used.
 */
function buildLensProposalPreview(
  tables: Array<LeDataContentBlock>,
  inlineBlocks: Array<LeDataContentBlock>,
  suggestion: LeDataLensCardSuggestion | undefined,
  visualType: LensCardType,
): LensProposalPreview | null {
  const table = [...tables].reverse().find((block) => normalizeTableBlock(block.payload).dataRows.length > 0);
  const chartRows = [...inlineBlocks]
    .reverse()
    .find(
      (block) =>
        block.type === 'chart' &&
        Array.isArray(block.payload.data) &&
        block.payload.data.length > 0 &&
        block.payload.data.every((row) => row && typeof row === 'object' && !Array.isArray(row)),
    );
  const chartTable = chartRows
    ? {
        id: `proposal-data-${chartRows.id}`,
        type: 'table' as const,
        payload: {
          columns: Object.keys((chartRows.payload.data as Array<Record<string, unknown>>)[0] ?? {}),
          rows: chartRows.payload.data,
        },
      }
    : undefined;
  const resultTable = table ?? chartTable;
  if (!resultTable) {
    return null;
  }

  const normalized = normalizeTableBlock(resultTable.payload);
  const rows = normalized.dataRows;
  const columns = normalized.headers.length ? normalized.headers : Object.keys(rows[0] ?? {});
  const measures = columns.filter((column) => isMeasureColumn(rows, column));
  const preferredMeasure =
    suggestion?.measure && measures.includes(suggestion.measure) ? suggestion.measure : measures[0];
  const preferredDimension = [suggestion?.timeField, suggestion?.dimension].find((field): field is string =>
    Boolean(field && columns.includes(field)),
  );
  const dimension =
    preferredDimension ??
    columns.find((column) => column !== preferredMeasure && !measures.includes(column)) ??
    columns[0];

  if (!dimension || !preferredMeasure) {
    return null;
  }

  const metric = resolveMetricHeadline(rows, columns);
  const metricPreview =
    metric.kind === 'value'
      ? {
          title: suggestion?.title ?? 'Proposed metric',
          value: formatLensValue(metric.value, fieldFormat(undefined, metric.column)),
          detail: metric.aggregate === 'sum' ? `Sum of ${metric.column.replace(/_/g, ' ')}` : undefined,
        }
      : null;
  const chartType = visualType === 'stacked' ? 'stackedbar' : visualType;
  const seriesKeys = visualType === 'stacked' ? measures : [preferredMeasure];

  return {
    table: resultTable,
    chart: {
      id: `proposal-${table.id}`,
      type: 'chart',
      payload: {
        title: suggestion?.title ?? 'Proposed chart',
        chartType,
        data: rows,
        xKey: dimension,
        yKey: preferredMeasure,
        seriesKeys,
      },
    },
    metric: metricPreview,
  };
}

function getReplacementTarget(
  blocks: Array<LeDataContentBlock>,
  suggestion: LeDataLensCardSuggestion | undefined,
  lensSaveTarget: Props['lensSaveTarget'],
  forcedReplacementCard?: Props['forcedReplacementCard'],
): { id: string; title: string } | undefined {
  if (!lensSaveTarget?.cards.length) {
    return undefined;
  }
  // Selection happens in the Lens canvas, so it is the authoritative signal.
  // Confirm the id still belongs to this Lens before presenting a replacement
  // action; a stale selection must never point at another dashboard.
  if (forcedReplacementCard) {
    const selected = lensSaveTarget.cards.find((card) => card.id === forcedReplacementCard.id);
    if (selected) {
      return selected;
    }
  }
  const explicit = suggestion?.replaceCardTitle?.trim().toLocaleLowerCase();
  if (explicit) {
    return lensSaveTarget.cards.find((card) => card.title.trim().toLocaleLowerCase() === explicit);
  }

  // Backwards-compatible recovery for a response that clearly says “use this
  // instead of <existing card>”, but was produced before the typed metadata.
  const answer = blocks
    .filter((block) => block.type === 'text')
    .map((block) => pickText(block.payload))
    .join('\n')
    .toLocaleLowerCase();
  const plainAnswer = answer.replace(/[*_`]/g, ' ');
  if (!/\b(instead of|replace|supersede)\b/.test(plainAnswer)) {
    return undefined;
  }
  // Do not turn an explanation such as “this does not supersede Total Bookings”
  // into a destructive-looking replacement action. Typed card metadata remains
  // the preferred signal; this prose fallback is deliberately conservative.
  if (
    /(?:\bdoes\s+not|\bdoesn't|\bdo\s+not|\bdon't|\bnot|\bnever)\s+(?:\w+\s+){0,2}(?:replace|supersede)\b/.test(
      plainAnswer,
    )
  ) {
    return undefined;
  }
  return lensSaveTarget.cards.find((card) => answer.includes(card.title.toLocaleLowerCase()));
}

/**
 * Whether the answer draws a chart, either as a dedicated `chart` block or as a ```chart fence
 * inside a text block. Drives auto-expand: an answer with no chart leans on the table for its
 * numbers, so the Data tab opens by default.
 */
export function messageHasChart(blocks: Array<LeDataContentBlock>): boolean {
  return blocks.some((block) => {
    if (block.type === 'chart') {
      return isRenderableChartPayload(block.payload);
    }
    if (block.type === 'text') {
      return hasRenderableChartFence(pickText(block.payload));
    }
    return false;
  });
}

/**
 * A result made up of individual operational records is useful to inspect,
 * filter and download, not to turn into a chart. A stray duration/count must
 * not turn a transcript, queue or customer record set into a single bar.
 *
 * Keep the rule deliberately conservative: a compact dated series or a
 * simple category-and-measure result can still be visualised; we only favour
 * the table when the result has the shape of a record list.
 */
function isRecordListTable(block: LeDataContentBlock): boolean {
  const table = normalizeTableBlock(block.payload);
  const rows = table.dataRows;
  const columns = table.headers.length ? table.headers : Object.keys(rows[0] ?? {});
  if (!rows.length || !columns.length) {
    return false;
  }

  const measures = columns.filter((column) => isMeasureColumn(rows, column));
  const dimensions = columns.filter((column) => !measures.includes(column));
  const hasDateSeries =
    dimensions.length <= 2 && dimensions.some((column) => rows.some((row) => isDateLikeText(row[column])));
  if (hasDateSeries) {
    return false;
  }

  const descriptiveColumns = dimensions.filter((column) =>
    rows.some((row) => {
      const value = row[column];
      return typeof value === 'string' && value.trim() !== '' && !isDateLikeText(value);
    }),
  );
  const hasRecordIdentity = dimensions.some((column) =>
    /(?:^|_)(?:id|bk|url|link|conversation|transcript|agent|queue|email|handle)(?:_|$)/i.test(column),
  );

  return descriptiveColumns.length >= 3 && (dimensions.length >= 3 || hasRecordIdentity);
}

/** True when an answer's useful primary artefact is its rows, rather than a chart. */
export function messagePrefersTable(blocks: Array<LeDataContentBlock>): boolean {
  // Keep ordinary table answers on their existing disclosure path. This is a
  // corrective display rule for the case where an upstream answer also tried
  // to draw a chart from an otherwise qualitative record set.
  const hasTable = blocks.some((block) => block.type === 'table');
  const hasInvalidChart = blocks.some((block) => block.type === 'chart' && !isRenderableChartPayload(block.payload));
  return (
    (messageHasChart(blocks) && blocks.some((block) => block.type === 'table' && isRecordListTable(block))) ||
    (hasTable && hasInvalidChart)
  );
}

/** The Data Tables tab label carries the shape of the result set: "Data Tables · 142 rows × 6 cols". */
export function getDataTabLabel(tableBlocks: Array<LeDataContentBlock>): string {
  if (tableBlocks.length !== 1) {
    return `Data Tables · ${tableBlocks.length}`;
  }

  const table = normalizeTableBlock(tableBlocks[0].payload);
  // Show the true total, not the capped inline preview, and flag when it is only a preview.
  const { totalRowCount } = table;
  const rows = `${totalRowCount.toLocaleString('en-US')} ${totalRowCount === 1 ? 'row' : 'rows'}${
    table.truncated ? ' (preview)' : ''
  }`;
  const cols = `${table.columnCount} ${table.columnCount === 1 ? 'col' : 'cols'}`;
  return `Data Tables · ${rows} × ${cols}`;
}

function buildEvidenceTabs(partitioned: PartitionedBlocks): Array<EvidenceTab> {
  const tabs: Array<EvidenceTab> = [];
  // Thought process leads (it explains how the answer was formed), then the data tables, then SQL.
  if (partitioned.thought.length > 0) {
    tabs.push({ id: 'reasoning', label: 'Thought Process', shortLabel: 'Thought process' });
  }
  if (partitioned.table.length > 0) {
    tabs.push({ id: 'data', label: getDataTabLabel(partitioned.table), shortLabel: 'Data' });
  }
  if (partitioned.sql.length > 0) {
    tabs.push({ id: 'sql', label: 'SQL', shortLabel: 'SQL' });
  }
  return tabs;
}

function downloadTableCsv(block: LeDataContentBlock) {
  const table = normalizeTableBlock(block.payload);
  if (!table.canDownload) {
    return;
  }

  const blockIdShort = block.id.slice(0, BLOCK_ID_FILENAME_LENGTH);
  const isoTimestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
  const filename = `askrichard-${blockIdShort}-${isoTimestamp}.csv`;
  downloadCsvFile(table.dataRows, table.headers, filename);
}

function getThoughtMarkdown(block: LeDataContentBlock): string {
  return pickText(block.payload);
}

function renderThoughtProcessContent(blocks: Array<LeDataContentBlock>): React.ReactNode {
  const seen = new Set<string>();
  return (
    <Box
      sx={(theme) => ({
        px: 1.75,
        py: 1.5,
        borderRadius: 1,
        // Theme tokens rather than fixed greys so the reasoning reads correctly in dark mode.
        bgcolor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        fontStyle: 'italic',
        '& > * + *': {
          mt: 1,
          pt: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        },
        '& p': {
          my: 0,
        },
      })}
    >
      {blocks.map((block) => {
        const markdown = getThoughtMarkdown(block).trim();
        if (!markdown || seen.has(markdown)) {
          return null;
        }

        seen.add(markdown);
        return (
          <LeDataMarkdownPreview
            key={block.id}
            content={markdown}
            boxProps={{
              sx: {
                fontSize: 'inherit',
                fontStyle: 'inherit',
                color: 'inherit',
              },
            }}
          />
        );
      })}
    </Box>
  );
}

function renderTabContent(
  activeTab: EvidenceTabId,
  partitioned: PartitionedBlocks,
  onKeepLiveSql?: (sqlText: string, blockId: string) => void,
  prominentLensSave = false,
  onSavedLens?: (lens: Lens) => void,
): React.ReactNode {
  if (activeTab === 'sql') {
    return partitioned.sql.map((block) => (
      <ContentBlockView
        key={block.id}
        block={block}
        onKeepLiveSql={onKeepLiveSql}
        onSavedLens={onSavedLens}
        prominentLensSave={prominentLensSave}
      />
    ));
  }

  if (activeTab === 'data') {
    // A single table's download lives in the card header; multiple tables keep their own so
    // every result set stays downloadable.
    if (partitioned.table.length === 1) {
      return <ContentBlockView key={partitioned.table[0].id} block={partitioned.table[0]} hideTableDownload />;
    }
    return partitioned.table.map((block) => <ContentBlockView key={block.id} block={block} />);
  }

  return renderThoughtProcessContent(partitioned.thought);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default function LeDataMessageBlocks({
  messageId,
  blocks,
  sourceQuestion,
  onSectionToggle,
  isStreaming = false,
  textStreaming = false,
  onKeepLiveSql,
  prominentLensSave: requestedProminentLensSave = false,
  lensSaveTarget,
  allowGlobalLensDrafts = false,
  forcedReplacementCard,
  cardApply,
  dashboardDestinations,
  onCreateLensCard,
  onBuildDashboard,
  onSavedLens,
  latestLensScopeBlockId,
  latestLensCreatedBlockId,
  lensTurnSending = false,
  lensTurnAnswerText,
  lensTurnQuestionAnswer,
  onSendLensTurn,
  onLensCreated,
  hideEvidence = false,
  frameless = false,
  evidenceOnly = false,
  evidenceTab,
  actionRow,
}: Props) {
  const canAccessLens = useCanAccessLeLens();
  const prominentLensSave = requestedProminentLensSave && canAccessLens;
  const theme = useTheme();
  const analyticsTokens = lensTokens(theme);
  const presentation = useMemo(() => parseLensPresentationEdits(blocks), [blocks]);
  const partitioned = useMemo(() => partitionBlocks(presentation.blocks), [presentation.blocks]);
  const tableFirstResult = useMemo(() => messagePrefersTable(blocks), [blocks]);
  // F12's own result to compare a pin against (01_USER_FLOWS.md), read off the blocks this
  // message already has: no fetch of its own, and undefined for anything but the common
  // one-query, one-table answer (see resolveChatResultForPin's own doc).
  const chatResult = useMemo(() => resolveChatResultForPin(blocks), [blocks]);
  const answerTitle = useMemo(() => answerTitleOf([...partitioned.table, ...partitioned.inline]), [partitioned]);
  const hasChart = useMemo(() => !tableFirstResult && messageHasChart(blocks), [blocks, tableFirstResult]);
  const primarySql = useMemo(() => getPrimarySql(partitioned), [partitioned]);
  const dashboardBrief = useMemo(() => dashboardBriefFromAnswer(blocks, sourceQuestion), [blocks, sourceQuestion]);
  const dashboardBriefTooLong = dashboardBrief.length > MAX_DASHBOARD_REVIEW_CONTEXT_LENGTH;
  // The pane's own apply action already names its one card as the forced subject; a caller need
  // not also pass forcedReplacementCard once it passes cardApply.
  const effectiveForcedReplacementCard =
    forcedReplacementCard ?? (cardApply ? { id: cardApply.card.id, title: cardApply.card.title } : undefined);
  const replacementTarget = useMemo(
    () =>
      getReplacementTarget(
        partitioned.inline,
        primarySql?.lensCardSuggestion,
        lensSaveTarget,
        effectiveForcedReplacementCard,
      ),
    [effectiveForcedReplacementCard, lensSaveTarget, partitioned.inline, primarySql?.lensCardSuggestion],
  );
  const matchedDashboard = useMemo(
    () => matchDashboardForSuggestion(primarySql?.lensCardSuggestion, dashboardDestinations),
    [dashboardDestinations, primarySql?.lensCardSuggestion],
  );
  const suggestedSaveType = primarySql
    ? tableFirstResult
      ? 'table'
      : (primarySql.lensCardSuggestion?.visualType ?? inferLensCardFromSql(primarySql.sql).type)
    : 'metric';
  // A returned result table is already the native, useful form. Do not
  // manufacture a chart chooser around it: the reader can inspect the rows
  // and save that exact table to a dashboard.
  const hasTableResult = partitioned.table.some((block) => normalizeTableBlock(block.payload).dataRows.length > 0);
  const resultRows = useMemo(() => {
    const primaryTable = partitioned.table[partitioned.table.length - 1];
    return primaryTable ? normalizeTableBlock(primaryTable.payload).totalRowCount : undefined;
  }, [partitioned.table]);
  const hasVerifiedAnswerAsset =
    !blocks.some((block) => block.type === 'error') && (Boolean(primarySql) || hasChart || hasTableResult);
  const tableOnlyProposal =
    prominentLensSave && (hasTableResult || tableFirstResult || primarySql?.lensCardSuggestion?.visualType === 'table');
  const tableFirstBlock = useMemo(() => {
    if (!tableFirstResult) {
      return undefined;
    }
    // Record lists already use the first branch. An invalid chart paired with
    // a compact aggregate should expose that aggregate as its table, not hide
    // both the chart and the data.
    return [...partitioned.table].reverse().find(isRecordListTable) ?? partitioned.table[partitioned.table.length - 1];
  }, [partitioned.table, tableFirstResult]);
  // The proposal is the reader-facing table, so do not render those same rows
  // a second time in the working shelf. SQL and thought process remain there.
  const evidencePartitioned = useMemo<PartitionedBlocks>(
    () => (tableOnlyProposal || tableFirstResult ? { ...partitioned, table: [] } : partitioned),
    [partitioned, tableFirstResult, tableOnlyProposal],
  );
  const tabs = useMemo(() => buildEvidenceTabs(evidencePartitioned), [evidencePartitioned]);
  const [saveType, setSaveType] = useState<LensCardType>(suggestedSaveType);
  const [showReplacement, setShowReplacement] = useState(false);
  const proposalPreview = useMemo(
    () =>
      prominentLensSave && primarySql
        ? buildLensProposalPreview(partitioned.table, partitioned.inline, primarySql.lensCardSuggestion, saveType)
        : null,
    [partitioned.inline, partitioned.table, primarySql, prominentLensSave, saveType],
  );

  // A later streaming block can identify the precise card to replace. Reset
  // the decision when that answer changes, but never overwrite a choice the
  // reader has just made while looking at the same answer.
  useEffect(() => {
    setSaveType(suggestedSaveType);
    setShowReplacement(false);
  }, [primarySql?.sql, replacementTarget?.id, suggestedSaveType]);

  // Auto state follows the blocks (so a streaming answer settles correctly); once the user
  // touches the card, their choice wins. `null` means "still following auto".
  const [userState, setUserState] = useState<{ open: boolean; tab: EvidenceTabId } | null>(null);

  const baseId = useId();
  const panelId = `${baseId}-evidence-panel`;

  const firstTabId = tabs[0]?.id;
  // The result and its Lens-save action lead. Evidence is always a deliberate
  // disclosure: even a chartless table can be read from the answer without
  // opening the shelf of SQL, raw data and reasoning beneath it.
  const autoOpen = false;
  const autoTab: EvidenceTabId = autoOpen ? 'data' : (firstTabId ?? 'sql');

  const presentIds = useMemo(() => new Set(tabs.map((tab) => tab.id)), [tabs]);
  const requestedTab = userState ? userState.tab : autoTab;
  const activeTab = presentIds.has(requestedTab) ? requestedTab : (firstTabId ?? autoTab);
  const isOpen = (userState ? userState.open : autoOpen) && tabs.length > 0;

  const singleTable = evidencePartitioned.table.length === 1 ? evidencePartitioned.table[0] : null;
  // normalizeTableBlock reconstructs dataRows, so memoize rather than repeat the work each render.
  const canDownloadSingleTable = useMemo(
    () => (singleTable ? normalizeTableBlock(singleTable.payload).canDownload : false),
    [singleTable],
  );

  const handleTabClick = (tabId: EvidenceTabId) => {
    onSectionToggle?.();
    // Clicking the open tab closes the card; any other click opens the card on that tab.
    if (tabId === activeTab && isOpen) {
      setUserState({ open: false, tab: tabId });
    } else {
      setUserState({ open: true, tab: tabId });
    }
  };

  const handleToggleOpen = () => {
    onSectionToggle?.();
    setUserState({ open: !isOpen, tab: activeTab });
  };

  const exportMutation = useReportingLeDataExport();
  const { enqueueSnackbar } = useSnackbar();

  const DOWNLOAD_UNAVAILABLE = 'The download is temporarily unavailable. Please try again shortly.';

  // Complete results download from the in-browser block; truncated ones fetch the full-result
  // GCS link from le-data rather than silently passing off the partial preview.
  const handleDownloadTable = async (block: LeDataContentBlock) => {
    const table = normalizeTableBlock(block.payload);
    if (!table.truncated) {
      downloadTableCsv(block);
      return;
    }
    if (!table.jobId) {
      enqueueSnackbar('This result is too large to download here — please re-run the query and try again.', {
        variant: 'error',
      });
      return;
    }
    try {
      enqueueSnackbar('Preparing your download…', { variant: 'info' });
      const { downloadUrl } = await exportMutation.mutateAsync({ jobId: table.jobId, format: 'csv' });
      if (!downloadUrl) {
        throw new Error(DOWNLOAD_UNAVAILABLE);
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const { status, responseBody } = (err ?? {}) as { status?: number; responseBody?: { message?: string } };
      // Prefer le-data's own message (proxied through svc-reporting); a bare gateway 504 has
      // no body, so give it a clean timeout line rather than a "Network error" string.
      const message =
        responseBody?.message ??
        (status === 504
          ? 'This export took too long — it may be very large. Narrow it with filters or fewer columns, then try again.'
          : DOWNLOAD_UNAVAILABLE);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const reducedMotion = prefersReducedMotion();
  const reducedMotionSx = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } } as const;

  if (evidenceOnly) {
    const selectedTab = evidenceTab && presentIds.has(evidenceTab) ? evidenceTab : firstTabId;
    return selectedTab ? (
      <Stack spacing={1.5} sx={{ minWidth: 0, maxWidth: '100%', width: '100%' }}>
        {renderTabContent(selectedTab, evidencePartitioned, onKeepLiveSql, false, onSavedLens)}
      </Stack>
    ) : null;
  }

  const hostOwnsActions = Boolean(actionRow);
  const displayedTable =
    proposalPreview && (tableOnlyProposal || saveType === 'table')
      ? proposalPreview.table
      : (tableFirstBlock ?? partitioned.table[partitioned.table.length - 1]);
  const proposalVisual = proposalPreview
    ? (cardTitle?: string, tableActions?: React.ReactNode) =>
        !tableOnlyProposal && saveType === 'metric' && proposalPreview.metric ? (
          <Paper
            aria-label={`Preview of ${cardTitle ?? proposalPreview.metric.title}`}
            variant="outlined"
            sx={{ px: 2, py: 2.5, textAlign: 'center' }}
          >
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              {cardTitle ?? proposalPreview.metric.title}
            </Typography>
            <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '2rem', lineHeight: 1.2, mt: 1 }}>
              {proposalPreview.metric.value}
            </Typography>
            {proposalPreview.metric.detail ? (
              <Typography color="text.secondary" sx={{ display: 'block', mt: 0.5 }} variant="caption">
                {proposalPreview.metric.detail}
              </Typography>
            ) : null}
          </Paper>
        ) : (
          <ContentBlockView
            block={
              tableOnlyProposal || saveType === 'table'
                ? proposalPreview.table
                : {
                    ...proposalPreview.chart,
                    payload: {
                      ...proposalPreview.chart.payload,
                      title: cardTitle ?? proposalPreview.chart.payload.title,
                    },
                  }
            }
            tableActions={tableActions}
            hideTableDownload={hostOwnsActions}
            chartHeight={200}
            frameless={frameless || hideEvidence}
            onSavedLens={onSavedLens}
          />
        )
    : null;
  // A table proposal is already the answer's primary, saveable result. If the model also wrote
  // the same figures as a Markdown table in its prose, keep the narrative but remove that duplicate
  // table. The full result remains visible here and its source rows remain available in evidence.
  const showsTableProposal = Boolean(proposalPreview && (tableOnlyProposal || saveType === 'table'));

  const lensDoorsOpen = prominentLensSave || (hostOwnsActions && canAccessLens);
  const canAddToLens = lensDoorsOpen && !isStreaming && primarySql && hasVerifiedAnswerAsset;
  const lensSaveActions =
    canAddToLens && !hostOwnsActions ? (
      <Stack
        alignItems="center"
        direction="row"
        flexWrap="wrap"
        gap={0.5}
        justifyContent="flex-end"
        sx={{ minWidth: 0 }}
      >
        <SaveSqlToLensButton
          accentColor={analyticsTokens.threadAccent}
          initialReplaceMode={false}
          preview={proposalVisual}
          preferredLensId={lensSaveTarget?.lensId}
          sql={primarySql.sql}
          onSavedLens={onSavedLens}
          answerTitle={answerTitle}
          sourceQuestion={sourceQuestion}
          resultRows={resultRows}
          actionLabel={
            lensSaveTarget?.lensId ? 'Add to this Lens' : hasChart ? 'Add chart to Lens' : 'Add result to Lens'
          }
        />
        {cardApply ? (
          showReplacement ? (
            <SaveSqlToLensButton
              accentColor={analyticsTokens.threadAccent}
              initialReplaceMode
              preferredLensId={lensSaveTarget?.lensId}
              replaceCard={{ id: cardApply.card.id, title: cardApply.card.title }}
              sql={primarySql.sql}
              onSavedLens={onSavedLens}
              answerTitle={answerTitle}
              sourceQuestion={sourceQuestion}
            />
          ) : (
            <Button size="small" onClick={() => setShowReplacement(true)}>
              Replace this card
            </Button>
          )
        ) : replacementTarget ? (
          <SaveSqlToLensButton
            accentColor={analyticsTokens.threadAccent}
            initialReplaceMode
            preferredLensId={lensSaveTarget?.lensId ?? matchedDashboard?.dashboard.lensId}
            replaceCard={replacementTarget}
            sql={primarySql.sql}
            onSavedLens={onSavedLens}
            answerTitle={answerTitle}
            sourceQuestion={sourceQuestion}
          />
        ) : null}
        {!cardApply && matchedDashboard && dashboardDestinations?.onOpenDashboard ? (
          <Button
            endIcon={<OpenInNewIcon />}
            onClick={() => dashboardDestinations.onOpenDashboard?.(matchedDashboard.dashboard.lensId)}
            size="small"
            sx={{ color: analyticsTokens.threadAccent }}
            variant="text"
          >
            View full Lens
          </Button>
        ) : null}
      </Stack>
    ) : null;
  const saveAction = lensSaveActions;
  const canBuildLens =
    lensDoorsOpen &&
    (hasTableResult || messageHasChart(blocks)) &&
    !blocks.some((block) => block.type === 'error') &&
    !isStreaming &&
    !presentation.proposals.length &&
    !blocks.some(
      (block) => block.type === 'lens_draft' || block.type === 'lens_applied' || block.type === 'lens_created',
    ) &&
    Boolean(onBuildDashboard);
  const startLensFromAnswer = () => {
    if (onSendLensTurn) {
      onSendLensTurn(BUILD_LENS_FROM_ANSWER_QUESTION, {
        brief: dashboardBrief,
        kind: 'create',
        sourceQuestion,
      });
      return;
    }
    onBuildDashboard?.(dashboardBrief, {
      hasExecutedResults: hasVerifiedAnswerAsset,
      sourceQuestion,
    });
  };
  const reviewAllResultsAction =
    canBuildLens && !hostOwnsActions ? (
      <Stack alignItems="flex-start" spacing={0.5} sx={{ minWidth: 0 }}>
        {dashboardBriefTooLong ? (
          <Alert severity="warning" sx={{ width: '100%' }}>
            This answer is too large to add as a group. Refine your question in chat, then choose the results to save.
          </Alert>
        ) : (
          <Button
            onClick={startLensFromAnswer}
            size="small"
            startIcon={<AddCardIcon />}
            sx={{ borderColor: analyticsTokens.threadAccent, borderRadius: 1, color: analyticsTokens.threadAccent }}
            variant="outlined"
          >
            Build Lens from this answer
          </Button>
        )}
      </Stack>
    ) : null;
  let saveActionPlaced = false;
  const hasProposalVisual = hasChart || Boolean(proposalPreview);
  const shownDraftBlockIds = shownLensDraftBlockIds(partitioned.inline, lensSaveTarget?.lensId, allowGlobalLensDrafts);
  const createdReceiptId = latestLensCreatedBlockId ?? latestCreatedReceiptId([{ blocks }]);

  return (
    <Stack spacing={0.75} sx={{ minWidth: 0, maxWidth: '100%', width: '100%' }}>
      {!isStreaming &&
        prominentLensSave &&
        presentation.proposals
          .filter((edit) => edit.lensId === lensSaveTarget?.lensId && (!cardApply || edit.cardId === cardApply.card.id))
          .map((edit) => (
            <LensPresentationProposal
              key={`${edit.lensId}-${edit.cardId}-${edit.baseVersion}`}
              edit={edit}
              onSavedLens={onSavedLens}
              hasUnsavedChanges={cardApply?.dirty}
            />
          ))}
      {/* The sentence that explains a change reads first; the staged change itself follows it,
          so the proposal block is drawn after the prose regardless of the order it streamed in. */}
      {[
        ...partitioned.inline.filter((block) => !RECEIPT_BLOCK_TYPES.has(block.type)),
        ...partitioned.inline.filter((block) => RECEIPT_BLOCK_TYPES.has(block.type)),
      ].map((block) => {
        if (block.type === 'lens_conflict') {
          return <LensConflictReceipt key={block.id} conflict={block.payload as unknown as LensConflict} />;
        }
        if (block.type === 'lens_filtered') {
          return <LensFilterReceipt key={block.id} receipt={block.payload as unknown as LensFiltered} />;
        }
        if (block.type === 'lens_applied') {
          const receipt = block.payload as unknown as LensAppliedChange;
          const destinationReceipt = [...blocks].reverse().find((candidate) => {
            if (candidate.type !== 'lens_applied') return false;
            const change = candidate.payload as unknown as LensAppliedChange;
            return change.applied && change.lensId === receipt.lensId;
          });
          return (
            <LensChangeReceipt
              key={block.id}
              openDestination={destinationReceipt?.id === block.id}
              receipt={receipt}
            />
          );
        }
        if (block.type === 'lens_draft') {
          return shownDraftBlockIds.has(block.id) ? (
            <LensProposalPreview key={block.id} />
          ) : null;
        }
        if (block.type === 'lens_scope') {
          return (
            <LensScopeBlock
              key={block.id}
              live={block.id === latestLensScopeBlockId}
              onSendTurn={onSendLensTurn ?? (() => {})}
              answeredText={lensTurnAnswerText}
              answeredQuestionAnswer={lensTurnQuestionAnswer}
              payload={block.payload as unknown as LensScopeBlockPayload}
              sending={lensTurnSending}
            />
          );
        }
        if (block.type === 'lens_created') {
          const createdLensId = isStreaming ? null : createdReceiptLensId(block, createdReceiptId);
          return createdLensId ? (
            <LensCreatedReceipt key={block.id} lensId={createdLensId} onOpenLens={onLensCreated} />
          ) : null;
        }
        // The proposal preview is the reader's editable visual. Do not leave
        // the model's original chart underneath it, otherwise choosing a new
        // chart type appears to have had no effect.
        if (
          (proposalPreview || tableFirstResult || !isRenderableChartPayload(block.payload)) &&
          block.type === 'chart'
        ) {
          return null;
        }
        const chartAction = block.type === 'chart' && !saveActionPlaced && !proposalPreview ? saveAction : undefined;
        if (chartAction) {
          saveActionPlaced = true;
        }
        return (
          // data-anchor-id gives LeDataChat's streaming read-position anchor a stable, block-level
          // target: unlike the message wrapper (whose own top never moves while blocks stream into
          // it), each of these moves down exactly when something is inserted above it.
          <Box key={block.id} data-anchor-id={block.id}>
            {/* A chart or saveable table proposal is the single formatted view. Drop any Markdown
              table duplicated in the prose; the narrative stays visible and source rows remain in evidence. */}
            <ContentBlockView
              block={block}
              hideInlineChartFences={Boolean(proposalPreview) || tableFirstResult}
              duplicateSql={prominentLensSave && chatResult ? primarySql?.sql : undefined}
              onKeepLiveSql={onKeepLiveSql}
              onSavedLens={onSavedLens}
              prominentLensSave={false}
              chartAction={chartAction}
              frameless={frameless || hideEvidence}
              streaming={textStreaming}
            />
          </Box>
        );
      })}

      {proposalPreview ? (
        <Stack data-testid="lens-proposal-preview" spacing={0.75} sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            {proposalVisual?.(
              undefined,
              showsTableProposal ? (
                <>
                  {lensSaveActions}
                  {reviewAllResultsAction}
                </>
              ) : undefined,
            )}
          </Box>
          {!showsTableProposal ? lensSaveActions : null}
          {!showsTableProposal ? reviewAllResultsAction : null}
        </Stack>
      ) : tableFirstBlock ? (
        <Stack data-testid="table-first-result" spacing={0.75} sx={{ minWidth: 0 }}>
          <ContentBlockView
            block={tableFirstBlock}
            hideTableDownload={hostOwnsActions}
            frameless={frameless || hideEvidence}
            onSavedLens={onSavedLens}
            tableActions={
              <>
                {lensSaveActions}
                {reviewAllResultsAction}
              </>
            }
          />
        </Stack>
      ) : null}

      {saveAction && hasProposalVisual && !saveActionPlaced && !proposalPreview ? (
        <Box sx={{ minWidth: 0, width: '100%' }}>{saveAction}</Box>
      ) : null}

      {reviewAllResultsAction && !proposalPreview && !tableFirstBlock ? reviewAllResultsAction : null}

      {saveAction && !hasProposalVisual ? (
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          spacing={1}
          useFlexGap
          sx={{
            mb: -1,
            borderTop: '1px solid',
            borderColor: 'divider',
            minWidth: 0,
            py: 1,
          }}
        >
          <Typography color="text.secondary" sx={{ flex: '1 1 200px', minWidth: 0 }} variant="caption">
            {cardApply
              ? `Add a new card, or choose to replace “${cardApply.card.title}”.`
              : replacementTarget
                ? `Add a new card, or explicitly replace “${replacementTarget.title}”.`
                : matchedDashboard?.source === 'current'
                  ? `Ready to add to ${matchedDashboard.dashboard.title}`
                  : matchedDashboard
                    ? `Related Lens: ${matchedDashboard.dashboard.title}`
                    : 'Ready to add to a Lens'}
          </Typography>
          {saveAction}
        </Stack>
      ) : null}

      {prominentLensSave && !isStreaming && !primarySql && hasVerifiedAnswerAsset && onCreateLensCard ? (
        <Stack alignItems="flex-start" spacing={0.5}>
          <Button onClick={onCreateLensCard} size="small" sx={{ color: analyticsTokens.threadAccent }}>
            Save this answer as one card
          </Button>
        </Stack>
      ) : null}

      {actionRow && !isStreaming
        ? actionRow({
            buildLens: canBuildLens && !dashboardBriefTooLong ? startLensFromAnswer : undefined,
            addToLens:
              canAddToLens && isExecutableLensSql(primarySql.sql)
                ? {
                    sql: primarySql.sql,
                    preview: proposalVisual,
                    preferredLensId: lensSaveTarget?.lensId,
                    onSavedLens,
                    answerTitle,
                    sourceQuestion,
                    resultRows,
                    accentColor: analyticsTokens.threadAccent,
                  }
                : undefined,
            downloadTable: displayedTable ? () => void handleDownloadTable(displayedTable) : undefined,
          })
        : null}

      {!actionRow && !isStreaming && blocks.some((block) => block.type === 'lens_applied') ? (
        <Stack direction="row" alignItems="center">
          <AnswerLensUndo blocks={blocks} />
        </Stack>
      ) : null}

      {!hideEvidence && tabs.length > 0 ? (
        <Paper
          data-anchor-id={`evidence-${messageId}`}
          variant="outlined"
          sx={{
            overflow: 'hidden',
            borderRadius: 1,
            borderColor: 'divider',
            boxShadow: 'none',
            bgcolor: 'transparent',
            ...(frameless ? { border: 0, borderTop: '1px solid', borderColor: 'divider', borderRadius: 0 } : null),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              minHeight: 32,
              borderBottom: isOpen ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Box
              role="tablist"
              aria-label="Evidence"
              sx={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
              }}
            >
              {tabs.map((tab) => {
                const active = tab.id === activeTab;
                // The Thought process tab reads as a different kind of thing from the SQL / Data Tables
                // text tabs, so it renders as a quiet outlined pill instead of an underlined text tab.
                const isReasoning = tab.id === 'reasoning';
                return (
                  <ButtonBase
                    key={tab.id}
                    role="tab"
                    id={`${baseId}-tab-${tab.id}`}
                    aria-selected={active}
                    aria-controls={panelId}
                    aria-expanded={active && isOpen}
                    onClick={() => handleTabClick(tab.id)}
                    sx={(theme) => ({
                      px: 1.25,
                      py: 0.5,
                      minHeight: 32,
                      whiteSpace: 'nowrap',
                      fontFamily: theme.typography.fontFamily,
                      fontSize: TAB_FONT_SIZE_PX,
                      fontWeight: 500,
                      color: active ? 'text.primary' : 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: active ? analyticsTokens.threadAccent : 'transparent',
                      transition: theme.transitions.create(['color', 'border-color', 'background-color'], {
                        duration: theme.transitions.duration.shortest,
                      }),
                      ...reducedMotionSx,
                      '&:hover': { color: 'text.primary' },
                      ...(isReasoning && {
                        alignSelf: 'center',
                        my: 0.5,
                        mx: 0.5,
                        px: 1,
                        minHeight: 24,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: active ? analyticsTokens.threadAccent : theme.palette.divider,
                        bgcolor: active ? 'action.selected' : 'transparent',
                        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                      }),
                    })}
                  >
                    {tab.label}
                  </ButtonBase>
                );
              })}
            </Box>

            {activeTab === 'data' && singleTable && canDownloadSingleTable ? (
              <Tooltip title="Download CSV">
                {/* span wrapper so the tooltip still works while the button is disabled */}
                <span>
                  <IconButton
                    size="small"
                    aria-label="Download CSV"
                    disabled={exportMutation.isPending}
                    onClick={() => {
                      void handleDownloadTable(singleTable);
                    }}
                    sx={{ alignSelf: 'center', mr: 0.25 }}
                  >
                    {exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}

            {!isOpen &&
            evidencePartitioned.table.length > 0 &&
            (evidencePartitioned.table.length > 1 || canDownloadSingleTable) ? (
              <Typography
                variant="caption"
                color="text.secondary"
                aria-hidden
                sx={{
                  alignSelf: 'center',
                  mr: 0.5,
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Expand to download CSV
              </Typography>
            ) : null}

            <IconButton
              size="small"
              aria-label={isOpen ? 'Collapse evidence' : 'Expand evidence'}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={handleToggleOpen}
              sx={{ alignSelf: 'center', mr: 0.25 }}
            >
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={(theme) => ({
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                  ...reducedMotionSx,
                })}
              />
            </IconButton>
          </Box>

          <Collapse in={isOpen} timeout={reducedMotion ? 0 : 'auto'} unmountOnExit>
            <Box
              id={panelId}
              role="tabpanel"
              aria-labelledby={`${baseId}-tab-${activeTab}`}
              sx={{ p: 1, bgcolor: 'background.paper' }}
            >
              <Stack spacing={1.5}>
                {renderTabContent(activeTab, evidencePartitioned, onKeepLiveSql, prominentLensSave, onSavedLens)}
              </Stack>
            </Box>
          </Collapse>
        </Paper>
      ) : null}
    </Stack>
  );
}
